<template>
  <div class="partner-widget-wrapper">
    <iframe
      ref="iframeRef"
      class="partner-widget-iframe"
      title="Партнёрский виджет"
      scrolling="no"
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
    />
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref } from 'vue';

  import { buildUrl } from '@/shared/utils/url';
  import { PARTNER_WIDGET_DEFAULT_FEATURES } from '@/shared/types/partnerWidget';

  interface IProps {
    price?: 'off' | 'on';
    ids?: string;
    experiences?: string | number[];
    city?: string;
    region?: string;
    country?: string;
    type?: string;
    generalTag?: string;
    template?: 'horizontal' | 'horizontal-list';
    features?: string[];
    num?: number;
    preContent?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    features: () => [...PARTNER_WIDGET_DEFAULT_FEATURES],
    preContent: false,
  });

  const iframeRef = ref<HTMLIFrameElement>();
  let resizeObserver: ResizeObserver | null = null;
  const syncTimers: ReturnType<typeof setTimeout>[] = [];

  const setHeight = (height: number) => {
    if (iframeRef.value && height > 0) {
      iframeRef.value.style.height = `${Math.ceil(height)}px`;
    }
  };

  const initLegacy = (iframe: HTMLIFrameElement) => {
    const widgetId = Math.floor(Math.random() * 1000000);
    const scriptId = `tripster-widget-${widgetId}`;

    const features = [...props.features];
    if (props.price === 'on') {
      features.push('price');
    }

    const experiencesCount = props.ids?.split(',').length || 3;

    const scriptUrl = `${buildUrl('https://experience.tripster.ru/partner/widget.js', {
      experiences: props.ids,
      city: props.city,
      region: props.region,
      country: props.country,
      type: props.type,
      tag: props.generalTag,
      view: 'experience',
      template: props.template ?? 'horizontal-list',
      mobile: 'list',
      order: 'top',
      width: '100%',
      num: experiencesCount,
      version: 2,
      partner: 'tripster_journal',
      features: features.join(','),
      script_id: scriptId,
      replace_missing_ids: true,
    })}`;

    iframe.onload = () => {
      const iframeDocument = iframe.contentDocument;
      if (!iframeDocument) {
        return;
      }

      const container = iframeDocument.createElement('div');
      container.id = `tripster-widget-container-${widgetId}`;
      const scriptElement = iframeDocument.createElement('script');
      scriptElement.id = scriptId;
      scriptElement.src = scriptUrl;
      scriptElement.async = true;
      iframeDocument.body.appendChild(container);
      container.appendChild(scriptElement);

      const syncHeight = () => {
        const nextHeight = Math.max(
          iframeDocument.documentElement?.scrollHeight ?? 0,
          iframeDocument.body?.scrollHeight ?? 0,
        );
        setHeight(nextHeight);
      };

      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(syncHeight);
      resizeObserver.observe(iframeDocument.body);

      syncHeight();
      scriptElement.onload = () => requestAnimationFrame(syncHeight);
      [500, 1500, 3000, 5000].forEach((delay) => syncTimers.push(setTimeout(syncHeight, delay)));
    };

    iframe.srcdoc = '<!DOCTYPE html><html><body></body></html>';
  };

  onMounted(() => {
    const iframe = iframeRef.value;
    if (iframe) {
      initLegacy(iframe);
    }
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    syncTimers.forEach(clearTimeout);
    syncTimers.length = 0;
  });
</script>

<style lang="scss" scoped>
  .partner-widget-wrapper {
    position: relative;
    box-sizing: border-box;
    margin: 24px -8px -8px -12px;
  }

  .partner-widget-iframe {
    display: block;
    width: 100%;
    min-height: 200px;
    border: none;
  }
</style>
